import intersectionBy from 'lodash/intersectionBy';

import { MADE_IN_UKRAINE_CHARACTERISTIC_GROUP_ID } from '@constants';

import { useProductScoreQuery } from './gql/graphql.v5.generated';
import {
  calculatePoints,
  getCoefficient,
  ProductData,
  MAX_PHOTO_POINTS,
  MAX_TITLE_POINTS,
  MAX_DESCRIPTION_POINTS,
  MAX_CATEGORY_POINTS,
  MAX_COLORS_POINTS,
  MAX_SIZES_POINTS,
  MAX_CHARACTERISTIC_POINTS,
  MAX_CONDITION_POINTS,
  MAX_PRICE_POINTS,
  MAX_KEYWORDS_POINTS,
} from './utils';

interface Payload extends ProductData {
  waitScore?: boolean,
}

export const useProductScore = (payload: Payload) => {
  const { data, loading } = useProductScoreQuery({
    variables: {
      slug: payload.catalogSlug,
    },
    context: {
      v5: true,
    },
    skip: !payload.catalogSlug,
  });

  const getFilteredCharacteristics = () => {
    return data?.catalog.characteristics.filter(
      (group) => (
        group.id !== MADE_IN_UKRAINE_CHARACTERISTIC_GROUP_ID
        && !group.excludeFromCompletionScale
      ),
    ) || [];
  };

  const calculateCoefficient = () => {
    if (!!payload.waitScore && loading) {
      return 0;
    }

    return getCoefficient({
      catalogSlug: payload.catalogSlug,
      colors: data?.filterColors,
      sizes: data?.filterSizes, 
      characteristics: getFilteredCharacteristics(),
    });
  };

  const calculateCurrentPoints = () => {
    const filteredCharacteristics = getFilteredCharacteristics();
    
    return calculatePoints({
      ...payload,
      characteristics: intersectionBy(payload.characteristics || [], filteredCharacteristics, 'id'),
    });
  };

  const coefficient = calculateCoefficient();
  const currentPoints = calculateCurrentPoints();

  return {
    coefficient,
    maxPhotoPercent: Math.round(MAX_PHOTO_POINTS * coefficient),
    maxDescriptionPercent: Math.round(MAX_DESCRIPTION_POINTS * coefficient),
    maxTitlePercent: Math.round(MAX_TITLE_POINTS * coefficient),
    maxCatalogPercent: Math.round(MAX_CATEGORY_POINTS * coefficient),
    maxPricePercent: Math.round(MAX_PRICE_POINTS * coefficient),
    maxConditionPercent: Math.round(MAX_CONDITION_POINTS * coefficient),
    maxColorsPercent: Math.round(MAX_COLORS_POINTS * coefficient),
    maxSizesPercent: Math.round(MAX_SIZES_POINTS * coefficient),
    maxCharacteristicPercent: Math.round(MAX_CHARACTERISTIC_POINTS * coefficient),
    maxKeywordPercent: Math.round(MAX_KEYWORDS_POINTS * coefficient),
    progress: Math.round(coefficient * currentPoints),
  };
};
